import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { useCarousel } from '@sentecacommerce-theme/lib'
import { CarouselArrowIcon } from '..'

export type CarouselProps = {
  renderSlide: (slide: any) => JSX.Element | null
  slides: any[]
  carouselConfig?: any
  arrowColor?: string
  spaceBetweenSlides?: string
}

export const Carousel = ({
  renderSlide,
  slides,
  carouselConfig = {},
  arrowColor,
  spaceBetweenSlides = '1.6rem',
}: CarouselProps) => {
  const [CarouselComponent, carousel] = useCarousel({
    containScroll: 'trimSnaps',
    align: 'start',
    slidesToScroll: 1,
    ...carouselConfig,
  })
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)

  const scrollPrev = useCallback(() => carousel?.scrollPrev(), [carousel])
  const scrollNext = useCallback(() => carousel?.scrollNext(), [carousel])

  useEffect(() => {
    if (carousel) {
      const onSelect = () => {
        setPrevBtnEnabled(carousel.canScrollPrev())
        setNextBtnEnabled(carousel.canScrollNext())
      }
      carousel.on('select', onSelect)
      onSelect()
    }
  }, [carousel])

  return (
    <Container>
      <SlidesContainer spaceBetweenSlides={spaceBetweenSlides}>
        <CarouselComponent>
          <SlidesWrapper spaceBetweenSlides={spaceBetweenSlides}>
            {slides.map((item, index) => (
              <SlideContainer
                spaceBetweenSlides={spaceBetweenSlides}
                key={`Slide__${index}`}
              >
                {renderSlide(item)}
              </SlideContainer>
            ))}
          </SlidesWrapper>
        </CarouselComponent>
      </SlidesContainer>
      <LeftArrow
        isEnabled={prevBtnEnabled}
        color={arrowColor}
        onClick={scrollPrev}
      />
      <RightArrow
        isEnabled={nextBtnEnabled}
        color={arrowColor}
        onClick={scrollNext}
      />
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  margin-top: -5rem;
`

const SlidesContainer = styled.div<{ spaceBetweenSlides?: string }>`
  margin-bottom: 0;
  position: relative;
  z-index: 1;
`

const SlidesWrapper = styled.div<{ spaceBetweenSlides?: string }>`
  padding-bottom: 0;
  overflow: visible;
  display: flex;
  margin-left: -${(props) => `${props.spaceBetweenSlides}`};
`

const SlideContainer = styled.div<{ spaceBetweenSlides?: string }>`
  position: relative;
  padding-left: ${(props) => `${props.spaceBetweenSlides}`};
  padding-top: 5rem;
  padding-bottom: 5rem;
`

interface IsActivatable {
  isEnabled: boolean
  color: string
}

const Arrow = styled(CarouselArrowIcon)<IsActivatable>`
  color: ${({ color, theme }) => (!color ? theme.palette.greyTitle : color)};
  width: 4rem;
  height: 4rem;
  position: absolute;
  top: 45%;
  display: ${(props) => (props.isEnabled ? 'block' : 'none')};
  z-index: 1000;

  cursor: pointer;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoints.sm.max}) {
    display: none;
  }
`
const LeftArrow = styled(Arrow)`
  left: 1.5rem;
  transform: rotate(180deg);
  @media (min-width: 1570px) {
    left: -6rem;
  }
`

const RightArrow = styled(Arrow)`
  right: 1.5rem;
  @media (min-width: 1570px) {
    right: -6rem;
  }
`
